import { Layers, FileText, Hash, Timer, Volume2, Flame } from 'lucide-react';

import Container from '../../Main/sections/Container';
import Reveal from '../../../components/Reveal';
import { useScrollProgress } from '../../../hooks/useScrollProgress';

const STEPS = [
  {
    n: '1',
    title: 'Cards are generated from your notes',
    body: 'Every key term, definition, date, and formula in your uploaded material becomes a flashcard automatically. A 30-page PDF becomes a 25-card deck in under 10 seconds.',
  },
  {
    n: '2',
    title: 'Study and rate each card',
    body: "Flip each card and rate yourself: 'Still learning', 'Almost' or 'Got it!' — your ratings feed the spaced repetition algorithm so hard cards come back sooner.",
  },
  {
    n: '3',
    title: 'Return daily, master everything',
    body: 'School Hack sends you a daily study reminder with only the cards due that day. Each session takes 5–10 minutes. Over time, every single card moves to "Mastered".',
  },
];

const N = STEPS.length;

/* ---------- right-side visuals ---------- */

const GenerateVisual = () => {
  const rows = [
    { icon: FileText, title: 'Interactive Notes', meta: '20 cards ready' },
    { icon: Hash, title: 'Key terms identified', meta: '47 terms' },
    { icon: Timer, title: 'Generation time', meta: '8 seconds' },
  ];
  return (
    <div className="card-dark rounded-2xl p-6">
      <div className="mb-4 flex items-center gap-2 text-white">
        <Layers size={18} className="text-accent" />
        <span className="text-sm font-semibold">Generating your flashcard deck…</span>
      </div>
      <div className="mb-4 rounded-xl border border-accent/20 bg-accent/[0.06] p-4 text-center">
        <p className="text-sm font-semibold text-white">Cards extracted from your notes</p>
        <p className="mt-1 text-[0.75rem] text-white/45">Biology Ch. 4 — Mitosis.pdf</p>
      </div>
      <div className="flex flex-col gap-2.5">
        {rows.map((r) => (
          <div
            key={r.title}
            className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/12 text-accent">
              <r.icon size={15} />
            </span>
            <span className="text-[0.85rem] font-medium text-white">{r.title}</span>
            <span className="ml-auto text-[0.72rem] text-white/45">{r.meta}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StudyRateVisual = () => (
  <div className="card-dark rounded-2xl p-6">
    <div className="mb-4 flex items-center justify-between text-white">
      <span className="text-sm font-semibold">Flash Cards</span>
      <span className="text-[0.75rem] text-white/45">2 / 10</span>
    </div>
    <div className="flex min-h-[150px] flex-col items-center justify-center gap-2 rounded-2xl border border-accent/30 bg-gradient-to-b from-accent/10 to-transparent p-6 text-center">
      <Volume2 size={16} className="text-accent" />
      <p className="text-[0.9rem] font-medium text-white">
        What is the capital city that houses Parliament Hill?
      </p>
      <span className="mt-1 text-[0.65rem] uppercase tracking-wide text-white/40">Tap to flip</span>
    </div>
    <div className="mt-4 flex gap-2">
      {[
        { label: 'Still learning', c: 'border-[#f87171]/50 text-[#f87171]' },
        { label: 'Almost', c: 'border-[#facc15]/50 text-[#facc15]' },
        { label: 'Got it!', c: 'border-accent/50 text-accent' },
      ].map((b) => (
        <span
          key={b.label}
          className={`flex-1 rounded-lg border bg-white/[0.02] py-2 text-center text-[0.72rem] font-medium ${b.c}`}
        >
          {b.label}
        </span>
      ))}
    </div>
  </div>
);

const MasteryVisual = () => {
  const stats = [
    { label: 'New', value: 8, w: '20%', c: 'bg-white/30' },
    { label: 'Learning', value: 12, w: '32%', c: 'bg-[#facc15]' },
    { label: 'Mastered', value: 27, w: '68%', c: 'bg-accent' },
  ];
  return (
    <div className="card-dark rounded-2xl p-6">
      <div className="mb-4 flex items-center gap-2 text-white">
        <Flame size={18} className="text-accent" />
        <span className="text-sm font-semibold">Your deck health</span>
        <span className="ml-auto rounded-full bg-accent/15 px-2.5 py-1 text-[0.65rem] font-semibold text-accent">
          12-day streak
        </span>
      </div>
      <div className="flex flex-col gap-3.5">
        {stats.map((s) => (
          <div key={s.label}>
            <div className="mb-1.5 flex justify-between text-[0.78rem]">
              <span className="text-white/70">{s.label}</span>
              <span className="font-semibold text-white">{s.value}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/8">
              <div className={`h-full rounded-full ${s.c}`} style={{ width: s.w }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 text-[0.78rem] text-white/70">
        <Timer size={14} className="text-accent" /> 15 cards due today · ~7 min
      </div>
    </div>
  );
};

const VISUALS = [GenerateVisual, StudyRateVisual, MasteryVisual];

const Header = () => (
  <div className="text-center">
    <span className="eyebrow">How It Works</span>
    <h2 className="font-heading mx-auto mt-5 max-w-3xl text-[2rem] font-bold leading-[1.1] sm:text-[2.5rem] lg:text-[2.9rem]">
      <span className="head-fade">Your Deck is Ready </span>
      <span className="text-accent-grad">Before You Even Ask</span>
    </h2>
    <p className="mx-auto mt-4 max-w-xl text-[0.95rem] text-white/55">
      The moment you upload your notes, flashcards are waiting. No setup, no
      manual work — just start studying.
    </p>
  </div>
);

const StepRow = ({ step, isActive, fill, onClick }) => (
  <div className="flex gap-4" style={{ opacity: isActive ? 1 : 0.55, transition: 'opacity 0.4s ease' }}>
    <div className="relative mt-1 w-[3px] shrink-0 rounded-full bg-white/10">
      <span
        className="absolute left-0 top-0 w-full rounded-full bg-accent"
        style={{ height: `${fill * 100}%`, transition: 'height 0.1s linear' }}
      />
    </div>
    <button onClick={onClick} className="text-left">
      <div className="flex items-center gap-3">
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[0.75rem] font-semibold transition-colors ${
            isActive ? 'border-accent bg-accent/10 text-accent' : 'border-white/20 text-white/50'
          }`}
        >
          {step.n}
        </span>
        <span
          className={`font-heading text-[1.05rem] font-semibold transition-colors md:text-[1.2rem] ${
            isActive ? 'text-accent' : 'text-white'
          }`}
        >
          {step.title}
        </span>
      </div>
      <p className="mt-2 pl-[2.5rem] text-[0.88rem] leading-relaxed text-white/55">{step.body}</p>
    </button>
  </div>
);

const FlashcardsHowItWorks = () => {
  const { ref, progress } = useScrollProgress();
  const scaled = progress * N;
  const index = Math.min(N - 1, Math.floor(scaled));

  return (
    <section ref={ref} className="relative pb-36 lg:h-[340vh]">
      {/* desktop pinned */}
      <div className="sticky top-0 hidden h-screen items-center overflow-hidden lg:flex">
        <div className="glow left-1/2 top-0 h-[420px] w-[700px] -translate-x-1/2 bg-accent/8" />
        <Container className="relative w-full">
          <Header />
          <div className="mt-12 grid grid-cols-[1fr_1fr] items-center gap-14">
            <div className="flex flex-col gap-9">
              {STEPS.map((s, i) => {
                const fill = i < index ? 1 : i === index ? Math.min(1, Math.max(0, scaled - i)) : 0;
                return (
                  <StepRow
                    key={s.n}
                    step={s}
                    isActive={i === index}
                    fill={fill}
                    onClick={() => {
                      const el = ref.current;
                      if (!el) return;
                      const distance = el.offsetHeight - window.innerHeight;
                      window.scrollTo({
                        top: el.offsetTop + ((i + 0.5) / N) * distance,
                        behavior: 'smooth',
                      });
                    }}
                  />
                );
              })}
            </div>
            <div className="relative h-[320px]">
              {VISUALS.map((V, i) => (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-500 ease-out"
                  style={{
                    opacity: i === index ? 1 : 0,
                    transform: i === index ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.98)',
                    pointerEvents: i === index ? 'auto' : 'none',
                  }}
                >
                  <V />
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* mobile stacked */}
      <div className="lg:hidden">
        <Container className="py-16">
          <Reveal>
            <Header />
          </Reveal>
          <div className="mt-10 flex flex-col gap-10">
            {STEPS.map((s, i) => {
              const V = VISUALS[i];
              return (
                <Reveal key={s.n} delay={40}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-accent bg-accent/10 text-[0.8rem] font-semibold text-accent">
                      {s.n}
                    </span>
                    <span className="font-heading text-[1.15rem] font-semibold text-accent">
                      {s.title}
                    </span>
                  </div>
                  <p className="mt-2 pl-11 text-[0.9rem] leading-relaxed text-white/60">{s.body}</p>
                  <div className="mt-5">
                    <V />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </div>
    </section>
  );
};

export default FlashcardsHowItWorks;
